import { useState } from 'react';
import { useReportMonitors } from '@/monitoring/useReportMonitors';
import {
  useMediaQuery,
  Text,
  useTheme,
  Tab,
  Tabs,
  styled,
  Box,
} from '@/theme/index';
import { UnclaimedMoneyResultsGrid } from './UnclaimedMoneyResultsGrid/UnclaimedMoneyResultsGrid';
import { UnclaimedMoneyResultsInformation } from './UnclaimedMoneyResultsInformation';
import { UnclaimedMoneySummaryBar } from './UnclaimedMoneySummaryBar';
import { MonitorReportButton } from '../components/MonitorReportButton';
import { MonitorSuccessModal } from '../components/MonitorSuccessModal';
import {
  UnclaimedMoneyResultsComponentProps,
  UnclaimedMoneyReportItem,
  UnclaimedMoneyRowsProps,
} from '../interfaces';

const MonitorContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
  padding: '6px 18px',
  marginBottom: '16px',
  borderRadius: '8px',

  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    backgroundColor: theme.palette.unclaimedMoneyMonitor?.main,
    marginTop: '18px',
    marginBottom: '0',
  },
}));

export function UnclaimedMoneyResultsMultiStates({
  firstName,
  lastName,
  noResultsImage,
  unclaimedMoneyReports,
  loadingBbqObject,
  bodyImage,
  states,
}: UnclaimedMoneyResultsComponentProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentReport, setCurrentReport] =
    useState<UnclaimedMoneyReportItem | null>(
      unclaimedMoneyReports[currentTabIndex] || null
    );

  const { reportIsMonitored, toggleReportIsMonitored } = useReportMonitors(
    currentReport?.permalink
  );

  const maxPageResults = 100;

  const [totalResults, setTotalResults] = useState(
    currentReport ? currentReport.totalResults : 0
  );
  const [pages, setPages] = useState(
    Math.min(currentReport ? currentReport.totalResults : 0, maxPageResults)
  );
  const [filterValue, setFilterValue] = useState('');
  const [unclaimedMoneyFilteredRows, setUnclaimedMoneyFilteredRows] =
    useState<UnclaimedMoneyRowsProps>();

  const handleTabChange = (event: React.SyntheticEvent, tabIndex: number) => {
    const selectedReport = unclaimedMoneyReports[tabIndex] || null;

    setCurrentTabIndex(tabIndex);
    setCurrentReport(selectedReport);
    setTotalResults(selectedReport?.totalResults || 0);
    setPages(Math.min(selectedReport?.totalResults || 0, maxPageResults));
  };

  const handleSetIsMonitored = () => {
    if (!reportIsMonitored) {
      setIsModalOpen(true);
    }
    toggleReportIsMonitored();
  };

  const closeModal = () => setIsModalOpen(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchQuery = e.target.value;
    setFilterValue(searchQuery);

    if (searchQuery === '') {
      setUnclaimedMoneyFilteredRows({ rows: [] });
      setTotalResults(currentReport?.totalResults || 0);
      setPages(Math.min(currentReport?.totalResults || 0, maxPageResults));
    } else {
      const filteredRows =
        currentReport?.unclaimedMoneyRows.rows.filter((row) =>
          Object.values(row).some((value) =>
            value.toString().toLowerCase().includes(searchQuery.toLowerCase())
          )
        ) || [];

      setUnclaimedMoneyFilteredRows({ rows: filteredRows });
      setTotalResults(filteredRows.length);
      setPages(Math.min(filteredRows.length, maxPageResults));
    }
  };

  return (
    <>
      <UnclaimedMoneySummaryBar
        firstName={firstName}
        lastName={lastName}
        totalResults={totalResults}
        pages={pages}
        filterValue={filterValue}
        handleInputChange={handleInputChange}
        isMobile={isMobile}
      />
      {!loadingBbqObject && (
        <>
          <Tabs
            value={currentTabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="scrollable unclaimed money states tabs"
            sx={{ marginBottom: '20px' }}
          >
            {states?.map((state, index) => (
              <Tab key={index} label={state} />
            ))}
          </Tabs>

          {currentReport && currentReport.totalResults > 0 ? (
            <>
              <MonitorContainer>
                <Text fontSize={14}>Stay updated on this search</Text>
                <MonitorReportButton
                  handleSetIsMonitored={handleSetIsMonitored}
                  isMonitored={reportIsMonitored}
                />
              </MonitorContainer>
              <UnclaimedMoneyResultsGrid
                unclaimedMoneyRows={
                  unclaimedMoneyFilteredRows || currentReport.unclaimedMoneyRows
                }
              />
            </>
          ) : (
            <UnclaimedMoneyResultsInformation
              informationImage={noResultsImage}
              title={
                currentReport
                  ? 'Sorry, no matches found at this time.'
                  : 'Sorry, your results are not ready yet.'
              }
              subTitle={
                currentReport
                  ? 'New unclaimed money gets added every day. Check back tomorrow'
                  : 'We are still processing your report. Please check back later or try refreshing the page.'
              }
              reloadAvailable={!currentReport}
              noResults
            />
          )}
        </>
      )}
      {currentReport && (
        <MonitorSuccessModal
          isOpen={isModalOpen}
          onClose={closeModal}
          firstName={firstName}
          lastName={lastName}
          bodyImage={bodyImage}
          states={currentReport.state}
        />
      )}
    </>
  );
}
